import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Slider from "@mui/material/Slider";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

function statusChipColor(status) {
  switch (status) {
    case "Departed":
      return "info";
    case "Arrived":
      return "success";
    case "Scheduled":
      return "warning";
    default:
      return "secondary";
  }
}

function AirportOperationsSection({ missionRows, organizations, livesTotal }) {
  const [range, setRange] = useState([0, 100]);

  const filtered = useMemo(() => {
    const n = missionRows.length;
    if (n === 0) return [];
    const span = 100 / n;
    const lo = Math.floor(range[0] / span);
    const hi = Math.ceil(range[1] / span);
    return missionRows.slice(lo, Math.max(lo + 1, hi));
  }, [missionRows, range]);

  const counts = useMemo(() => {
    const total = missionRows.length;
    const departed = missionRows.filter((m) => m.status === "Departed").length;
    const arrived = missionRows.filter((m) => m.status === "Arrived").length;
    return { total, departed, arrived };
  }, [missionRows]);

  return (
    <SoftBox
      sx={{
        borderRadius: "1.25rem",
        border: "none",
        boxShadow: "0 4px 24px rgba(52, 71, 103, 0.07)",
        overflow: "hidden",
        background: `linear-gradient(145deg, ${colors.white.main} 0%, ${colors.grey[100]} 100%)`,
      }}
    >
      <SoftBox
        px={{ xs: 3, md: 3.5 }}
        py={3}
        sx={{
          borderBottom: `1px solid ${rgba(colors.dark.main, 0.06)}`,
          background: `linear-gradient(90deg, rgba(23, 193, 232, 0.08) 0%, rgba(0, 32, 84, 0.04) 100%)`,
        }}
      >
        <SoftTypography variant="h6" fontWeight="bold" mb={1} sx={{ lineHeight: 1.35 }}>
          Airport operations
        </SoftTypography>
        <SoftTypography variant="caption" color="text" sx={{ lineHeight: 1.55 }}>
          Missions your airport has supported — filter the table with the range slider (demo)
        </SoftTypography>
      </SoftBox>
      <SoftBox p={{ xs: 3, md: 4 }}>
        <SoftBox display="flex" flexWrap="wrap" gap={2} mb={3}>
          {[
            { label: "Total missions", value: counts.total, c: "dark" },
            { label: "Departed", value: counts.departed, c: "info" },
            { label: "Arrived", value: counts.arrived, c: "success" },
            { label: "Lives impacted (to/from)", value: livesTotal, c: "success" },
          ].map((k) => (
            <SoftBox
              key={k.label}
              px={2.25}
              py={2}
              borderRadius="lg"
              sx={{
                backgroundColor: "white.main",
                border: "none",
                boxShadow: "0 2px 12px rgba(52, 71, 103, 0.06)",
                minWidth: "10rem",
              }}
            >
              <SoftTypography variant="caption" color="text" display="block" fontWeight="medium">
                {k.label}
              </SoftTypography>
              <SoftTypography variant="h5" fontWeight="bold" color={k.c}>
                {k.value}
              </SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>
        <SoftBox mb={2}>
          <SoftTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
            Date range (sample index)
          </SoftTypography>
          <Slider
            value={range}
            onChange={(e, v) => setRange(v)}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            sx={{ maxWidth: "min(100%, 420px)" }}
          />
        </SoftBox>
        <TableContainer
          sx={{
            borderRadius: "lg",
            border: "none",
            boxShadow: "inset 0 0 0 1px rgba(52, 71, 103, 0.06)",
            backgroundColor: "white.main",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mission</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Lives</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    <Chip label={row.status} color={statusChipColor(row.status)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {row.origin} → {row.dest}
                  </TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell align="right">{row.lives}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <SoftBox mt={3}>
          <SoftTypography variant="button" fontWeight="bold" color="dark" display="block" mb={1.5}>
            Organizations supported this month
          </SoftTypography>
          <SoftBox display="flex" flexDirection="column" gap={1}>
            {organizations.map((org) => (
              <Card
                key={org.name}
                sx={{
                  borderRadius: "lg",
                  border: "none",
                  boxShadow: "0 2px 12px rgba(52, 71, 103, 0.06)",
                }}
              >
                <SoftBox px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <SoftBox>
                    <SoftTypography variant="button" fontWeight="bold" display="block">
                      {org.name}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">
                      {org.type}
                    </SoftTypography>
                  </SoftBox>
                  <Chip label={`${org.missions} missions`} size="small" color="primary" variant="outlined" />
                </SoftBox>
              </Card>
            ))}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

AirportOperationsSection.propTypes = {
  missionRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      missions: PropTypes.number.isRequired,
    })
  ).isRequired,
  livesTotal: PropTypes.number.isRequired,
};

export default AirportOperationsSection;
